import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface, UserServiceInterface } from '@hutsix/ngxh6';
import { passwordMatchValidator } from '../../../../../validators/password-match.validator';

/**
 * Replace the temporary password TLC staff issued, on first sign-in.
 *
 * Deliberately not the profile form. That form requires an email address and offers
 * the password as optional ("leave empty if you don't want to change the password") -
 * so it invited a new user to edit the identity TLC had just given them, on the one
 * screen the guard would not let them leave. This does one thing.
 */
@Component({
    selector: 'app-set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SetPasswordComponent implements OnDestroy {
    private unsubscribe$ = new Subject<void>();

    public form: FormGroup;
    public error?: string;
    public loading = false;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        @Inject('UserService') private userService: UserServiceInterface,
        private cdRef: ChangeDetectorRef,
        private router: Router,
        formBuilder: FormBuilder,
    ) {
        this.form = formBuilder.group(
            {
                password: ['', [Validators.required, Validators.minLength(10)]],
                repeatPassword: ['', [Validators.required]],
            },
            { validator: passwordMatchValidator('password', 'repeatPassword') },
        );
    }

    public submit(): void {
        this.loading = true;
        this.api.post({ url: '/api/user/set_password', data: { password: this.form.get('password').value } }).subscribe(
            (res: ApiResponse<any>) => {
                // Re-read the account so mustChangePassword goes down client-side too,
                // otherwise the guards bounce us straight back here.
                this.api.get({ url: '/api/v1/user/current', useCache: false }).subscribe((current: any) => {
                    this.userService.updateUserData(current.data);
                    // Land where signing in would have landed them. Everyone reaching
                    // this screen was given a temporary password by someone else, so
                    // they arrive without ever having chosen a destination - and the
                    // public home page is the wrong one for the two kinds of account
                    // that get issued passwords this way.
                    this.router.navigate([this.defaultRoute()]);
                });
            },
            (err: ApiErrorResponse<any>) => {
                this.loading = false;
                this.error = err.error?.error ?? 'Your password could not be set. Please try again.';
                this.cdRef.detectChanges();
            },
        );
    }

    /**
     * Where to go once the password is set.
     *
     * Deliberately the same rule as the login screen's: a business login lands in
     * /business and staff in /crm. Two copies that could disagree would be worse than
     * one - if this ever needs to change, it should change in both.
     */
    private defaultRoute(): string {
        if (this.userService.isGranted(['ROLE_ADMIN', 'ROLE_STAFF'])) return '/crm';
        if (this.userService.isGranted(['ROLE_BUSINESS'])) return '/business';

        return '/';
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
